import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import StatusChip from "@/components/StatusChip";
import { inr, fmtDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { FilePlus2, FileSearch, ShieldCheck } from "lucide-react";

export default function IndividualDashboard() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/cases").then((r) => setCases(r.data)).finally(() => setLoading(false));
  }, []);

  const open = cases.filter(c => !["Resolved", "Closed"].includes(c.status)).length;
  const resolved = cases.filter(c => ["Resolved", "Closed"].includes(c.status)).length;

  return (
    <div className="space-y-8" data-testid="individual-dashboard">
      <div className="grid sm:grid-cols-3 gap-4">
        <KPI label="Total cases" value={cases.length} />
        <KPI label="Open" value={open} />
        <KPI label="Resolved" value={resolved} accent />
      </div>

      <div className="rounded-sm bg-gradient-to-br from-primary to-primary/80 text-court-foreground p-8 flex flex-wrap gap-6 items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-secondary font-semibold">Start something new</div>
          <h2 className="font-display text-3xl mt-2">Have a dispute on your mind?</h2>
          <p className="opacity-85 mt-2 max-w-md">File it online with eSulah. We'll guide you through evidence, mediator/arbitrator assignment and a fair hearing.</p>
        </div>
        <Link to="/app/file" data-testid="cta-file-dispute">
          <Button size="lg" className="rounded-sm bg-secondary hover:bg-secondary/90 text-secondary-foreground h-12 px-7"><FilePlus2 className="h-4 w-4 mr-2" />File a Dispute</Button>
        </Link>
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-2xl text-court">My cases</h3>
          <Link to="/app/cases" className="text-sm text-court hover:underline">View all →</Link>
        </div>
        {loading ? <div className="text-muted-foreground">Loading…</div> : cases.length === 0 ? (
          <div className="rounded-sm border border-dashed border-stone/60 p-12 text-center text-muted-foreground bg-card">
            <FileSearch className="h-10 w-10 mx-auto mb-3 opacity-60" />
            No cases yet. File your first dispute to get started.
          </div>
        ) : (
          <div className="grid gap-3">
            {cases.slice(0, 5).map((c) => (
              <Link to={`/app/cases/${c.id}`} key={c.id} data-testid={`case-row-${c.case_number}`} className="bg-card rounded-sm border border-stone/60 p-5 flex flex-wrap gap-4 items-center justify-between lift-on-hover">
                <div>
                  <div className="text-xs text-muted-foreground">{c.case_number}</div>
                  <div className="font-semibold text-court mt-0.5">{c.title}</div>
                  <div className="text-xs text-muted-foreground mt-1">Filed {fmtDate(c.created_at)} · {c.type}</div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="text-xs text-muted-foreground">Claim</div>
                    <div className="font-semibold">{inr(c.claim_amount)}</div>
                  </div>
                  <StatusChip status={c.status} />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function KPI({ label, value, accent }) {
  return (
    <div className={`rounded-sm p-6 border ${accent ? "bg-emerald-50 border-emerald-200" : "bg-card border-stone/60"} `} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className="font-display text-4xl font-semibold mt-2 text-court">{value}</div>
    </div>
  );
}
